import React from 'react';
import clsx from 'clsx';
import { IntlShape, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../common/types';
import { platformMeta } from '../promote/platformMeta';
import { ChannelPostTotals } from '../promote/socialNumbers';
import { SerializedSocialChannel } from '../promote/types';
import { channelNumbersState } from '../promote/channels/channelNumbersState';
import { SocialTotalStateLine } from './SocialTotalStateLine';
import { followersLabelMessage } from '../promote/channels/channelNumbersMessages';
import { socialMessages as messages } from './socialMessages';
import { Panel, PanelBody, PanelHead } from '../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../promote/Promote.module.scss';
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialTotalCardProps {
  channel: SerializedSocialChannel;
  /** The views and the engagement of the posts of the period. Undefined while the posts load. */
  totals?: ChannelPostTotals;
  /** True when the period has more posts than the app reads. The sums then end with a "+". */
  isCapped?: boolean;
}

/** A short number: "112.9K". A number that the platform does not give is a dash, never 0. */
function compact(intl: IntlShape, value: number | null | undefined, isLowerLimit = false): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className={shared.dash}>—</span>;
  }
  const text = intl.formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 });
  // A sum over a part of the posts is a lower limit: "112.9K+".
  return isLowerLimit ? `${text}+` : text;
}

/**
 * The totals of one channel: followers and their change, views and engagement (design b1). A
 * channel that needs a new sign-in keeps a plain line with no button: Social has no connect
 * control (design your-intelligence g1).
 */
const SocialTotalCardComponent: React.FC<SocialTotalCardProps> = ({ channel, totals, isCapped = false }) => {
  const intl = useIntl();
  const meta = platformMeta[channel.platform];
  const state = channelNumbersState(channel);
  const change = channel.followersChange;

  return (
    <Panel className={styles.channelTotal} data-testid={`social-total-${channel.platform}`}>
      <PanelHead
        icon={
          <span className={styles.totalMark} style={{ color: meta.color }}>
            <FontAwesomeIcon icon={meta.icon} />
          </span>
        }
        title={meta.name}
        right={<small className={styles.totalHandle}>{channel.handle ?? channel.connection?.displayName ?? ''}</small>}
      />
      <PanelBody className={styles.totalBody}>
        {state === 'numbers' ? (
          <div className={styles.totalNumbers}>
            <div>
              <div className={styles.totalLabel}>{intl.formatMessage(followersLabelMessage(channel.platform))}</div>
              <span className={styles.totalFollowers}>{compact(intl, channel.followers)}</span>
              {channel.followers !== null && change !== null && change !== 0 ? (
                <span className={clsx(styles.totalChange, change < 0 && styles.totalChangeNeg)}>
                  {intl.formatNumber(change, { signDisplay: 'exceptZero' })}
                </span>
              ) : null}
            </div>
            <div>
              <div className={styles.totalLabel}>{intl.formatMessage(messages.views)}</div>
              <div className={styles.totalValue}>{compact(intl, totals?.views, isCapped)}</div>
            </div>
            <div>
              <div className={styles.totalLabel}>{intl.formatMessage(messages.engagement)}</div>
              <div className={styles.totalValue}>{compact(intl, totals?.engagement, isCapped)}</div>
            </div>
            {/* Only X gives link clicks (design x-connector e2). */}
            {totals?.linkClicks === null || totals?.linkClicks === undefined ? null : (
              <div data-testid={`social-total-${channel.platform}-link-clicks`}>
                <div className={styles.totalLabel}>{intl.formatMessage(messages.linkClicks)}</div>
                <div className={styles.totalValue}>{compact(intl, totals.linkClicks, isCapped)}</div>
              </div>
            )}
          </div>
        ) : (
          <SocialTotalStateLine channel={channel} state={state} />
        )}
      </PanelBody>
    </Panel>
  );
};

export const SocialTotalCard = TypedMemo(SocialTotalCardComponent);
